import { Component } from '@angular/core';
import { OnInit, OnDestroy, Input, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { Subscription, Observable, firstValueFrom } from 'rxjs';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../../services/config.service';
import { UserService } from '../../../services/user.service';
import { AlertsService } from '../../../services/alerts.service';
import { BlockUIService } from '../../../layout/block-ui/block-ui.service';
import { SearchService } from '../../../search/services/search.service';
import { ViewerService } from '../../../services/viewer.service';
import { ProductService } from '../../../services/product.service';
import { MetadataService } from '../../../services/metadata.service';
import { MapService } from '../../../map/services/map.service';
import { AlbumsService } from '../../../services/albums.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { FeatureService } from '../../../services/feature.service';
import { RoutingService } from '../../../services/routing.service';
import { ConfirmComponent } from '../../../components/confirm/confirm.component';
import { RecordQuickViewComponent } from '../../../records-management/components/record-quick-view/record-quick-view.component';
import { ProductShareComponent } from '../../../share/product-share/product-share.component';
import { AlbumItemComponent } from '../../../albums/album-item/album-item.component';
import { EmailProductsComponent } from '../../../components/email-products/email-products.component';
import { RevisionHistoryComponent } from '../../../components/revision-history/revision-history.component';
import { ThumbnailLoaderComponent } from '../../../components/thumbnail-loader/thumbnail-loader.component';
import { CitationComponent } from '../../../components/citation/citation.component';
import { SearchTermsComponent } from '../../../components/search-terms/search-terms.component';
import { ProductFeedbackComponent } from '../../../components/product-feedback/product-feedback.component';
import { GeoTagComponent } from '../../../map/geo-tag/geo-tag.component';
import { CopyLinkComponent } from '../../../components/copy-link/copy-link.component';
import { DocViewComponent } from '../../../views/doc-view/doc-view.component';

import { PageDescription } from '../../../models/viewer.interface';

import { formatString } from '../../../utils/string-utils';

import cloneDeep from 'lodash/cloneDeep';
import dayjs from 'dayjs';


/**
 * Custom Feature: Displays a memorial view for ABMC records.
 */
@Component({
	selector: 'doc-memorial-view',
	templateUrl: './doc-memorial-view.component.html',
	styleUrl: './doc-memorial-view.component.css'
})
export class DocMemorialViewComponent implements OnInit, OnDestroy {
	@Input() productId: any;
	@Input() pageInfo: any;

	private userEventSubscription!: Subscription;
	private searchEventSubscription!: Subscription;
	private routingEventSubscription!: Subscription;

	@ViewChild('pageFileInput') pageFileInputRef!: ElementRef<HTMLInputElement>;
	
	Object = Object;
	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};
	dateFormatDisplay: string = 'MM/DD/YY';

	product: { id: number; title: string; } = {
		id: 0,
		title: ''
	};

	pdfView: boolean = false;
	panelOn: boolean = false;
	panelMode: string | null = null;
	isRecord: boolean = false;
	shareLinkOn: boolean = false;
	pageMetadataEdit: boolean = false;
	citationsEnabled: boolean = false;
	mapViewEnabled: boolean = false;

	internalFilters: string[] = [];
	filters: string[] = [];
	filterInput: string | null = null;

	page: any = {};
	pageDescription: PageDescription = {
		type: null,
		mime: null,
		currView: 'metadata'
	}

	hasPrev: boolean = false;
	hasNext: boolean = false;

	albumView: number | null = null; // This is the album id
	album: any = {};

	pages: any[] = [];
	currentPage: number = 1;
	selectedPage: number = 0;
	finalIndex: number = 0;
	index: boolean = false;
	status: boolean = false;
	count: number = 0;
	docPerm: string | null = null;
	_accessLevel: number = 0;

	documentData: { [key: string]: any } = {};
	fullRecordMetadata: { [key: string]: any } = {};
	productPageMetadata: { [key: string]: any } = {};
	copyPageMetadata: { [key: string]: any } = {};
	currTag: string | null = null;

	searchResults: { total: number; _index: number; current: number, records: any[]; } = {
		total: 0,
		_index: 1,
		current: 1,
		records: []
	};

	// TODO do we need this?
	package: { packagePage: number; page: number; pages: any[]; } = {
		packagePage: -1,
		page: -1,
		pages: []
	};

	ocr: { available: boolean; viewerURL: SafeResourceUrl | undefined; } = {
		available: false,
		viewerURL: undefined
	};

	downloadDocumentLink: string | null = null;
	downloadDocumentPageLink: string | null = null;

	loading$: Observable<boolean>;
	message$: Observable<string>;
	
	// Memorial view-specific
	custId: string = '';
	featureId: string = 'MemorialDocViewer';
	
	cemeteries: any = {};
	socials: any = {};
	record: { [key: string]: any } = {};
	searchableMetadataFields: any[] = [];
	@ViewChild('carouselViewer') carouselViewerRef!: ElementRef<HTMLInputElement>;
	currentCarouselPageIndex: number = 0;

	constructor(
		private ngbModal: NgbModal,
		private ngbmodalService: NgbActiveModal,
		private blockUI: BlockUIService,
		private userService: UserService,
		private configService: ConfigService,
		private alertsService: AlertsService,
		private searchService: SearchService,
		private mapService: MapService,
		private viewerService: ViewerService,
		private productService: ProductService,
		private metadataService: MetadataService,
		private albumsService: AlbumsService,
		private analyticsService: AnalyticsService,
		private featureService: FeatureService,
		private routingService: RoutingService,
		private http: HttpClient
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
		this.loading$ = blockUI.loading;
		this.message$ = blockUI.message;
	}

	ngOnInit(): void {
		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if (event === 'loggedin') {
				this.checkDownloadAccess();
			}
			else if (event === 'localizationLoaded') {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			}
		});

		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe((event: any) => {
			if (event.type === 'searchEnded') {
				console.log('Search Ended event received!');
				this.processSearchResults();
			}
			else if (event.type === 'searchResultField' || event.type === 'openBrowseLink') {
				console.log('Search Result Field event received!');
				if (event.data?.source === 'metadata-view') {
					this.close();
				}
			}
		});
		
		this.routingEventSubscription = this.routingService.routingEvent.subscribe((event: any) => {
			if (event.eventType === 'navigationEnded') {
				// User interacted with the navigation component links
				this.close();
			}
		});	
		
		this.custId = this.featureService.getCustomerId();
		this.loadPageAssets();	

		let dateTypeProperty = this.userService.user.properties.awpropertyObjectList.find(function(x: any) { return x.name === "USER_INTERFACE_DATE_TYPE"; });
		this.dateFormatDisplay = dateTypeProperty?.value || 'MM/DD/YY';

		this.searchableMetadataFields = this.configService.getConfig().search.searchableMetadataFields;
		this.mapViewEnabled = this.configService.getConfig().esri.enabled;
		this.citationsEnabled = this.configService.getConfig()?.menu?.citations;
		this.shareLinkOn = this.configService.getShareLinkState();

		this.checkDownloadAccess();
		this.initializeViewer();
		this.initializeABMCConfiguration();
		this.loadDocument(this.productId, this.pageInfo);
	}

	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}

		if (this.searchEventSubscription) {
			this.searchEventSubscription.unsubscribe();
		}
		
		if (this.routingEventSubscription) {
			this.routingEventSubscription.unsubscribe();
		}		
	}

	private initializeViewer(): void {
		// TODO: If there are no results, close or switch to error state
		this.searchResults.total = this.searchService.results?.total || 0;
		this.searchResults.records = this.searchService.results?.records || [];

		for (let i = 0; i < this.searchResults.records.length; i++) {
			if (this.productId == this.searchResults.records[i].id) {
				this.searchResults._index = i + 1;
				this.searchResults.current = this.searchResults._index + ((this.searchService.pageIndex - 1) * this.searchService.pageSize);
				break;
			}
		}

		// Is this document from an album search result?
		// If so, the entire result set is in 'album mode'
		const albumContent = this.searchService.criteria?.['album_id']?.content;
		this.albumView = Array.isArray(albumContent) && albumContent.length > 0
			? (albumContent[0]?.content || albumContent[0])
			: null;


		// Set up any internal page filters
		if (this.configService.getConfig()?.viewer?.pageRoleBasedViewEnabled
			&& !(this.userPermissions['ROLE_PRODUCT_VIEW_INTERNAL_PAGE'] && this.userPermissions['ROLE_PRODUCT_VIEW_INTERNAL_PAGE'] == 1)) {
			this.internalFilters.push("internal");
		}
	}

	/**
	 * Loads the given product in the doc viewer.
	 */
	private loadDocument(id: number, pageInfoInput?: any): void {
		setTimeout(() => {
			this.blockUI.start(this.LOCALIZATION.loading);
		});

		this.viewerService.processDocument(id, pageInfoInput, this.searchService.query).then(
			(results: any) => {

				console.log("Inside loadDocument, id: %s", id);
				console.log(results);

				// NOTE: product must be a new object reference to work reliably with ngOnChanges in the sub-views
				this.product = {
					id: id,
					title: results.title
				};

				this.pdfView = false; // why do we need this. standardize this to use pageDescription

				this.documentData = results.documentData;
				this.documentData['projected'] = results.projected;

				this.count = results.count;
				this.finalIndex = results.finalIndex;
				this.fullRecordMetadata = results.fullRecordMetadata;
				
				// FIXME: FOR TESTING ONLY - REMOVE ME
//				this.fillTESTMetadata();
				// END: REMOVE ME
				
				this.index = results.index;
				this.pages = results.pages;
				this.selectedPage = results.selectedPage;
				this.status = results.selectedPage;
				this._accessLevel = this.userService.getProductAccessLevel(this.fullRecordMetadata);
				this.downloadDocumentLink = formatString(this.configService.getConfig().document.documentDownloadURL, this.product.id);

				if (this.documentData['productAccessType'] === "PROHIBITED") {
					this.alertsService.error({
						title: "Prohibited Document",
						message: this.LOCALIZATION.accessProhibitedDoc,
						delay: 5000
					});
					this.close();
				}

				if (this.userPermissions['ROLE_PRODUCT_VIEW'] && this.documentData?.['linksMap']?.OCR_PDF) {
					this.ocr.available = true;

					let ocrURL = `/aw-server/rest/service/awdocumentprovider/uuid/${this.product.id}`;
					ocrURL += `/page/0/sheet/0/type/STANDARD/OCRPDF`;
					ocrURL += `?searchString=${this.searchService.query}`;

					this.ocr.viewerURL = this.viewerService.generateViewerURL(ocrURL);
				} else {
					this.ocr.available = false;
					this.ocr.viewerURL = undefined;
				}

				this.loadRMRecord();
				this.processPages();
				this.processVisited();
				this.loadSocialMediaForProduct();
				this.loadRecord();
				this.initCarousel();

				let userData = this.userService.getLocalStorageData();
				if (userData.viewer.panelOn) {
					this.panelMode = userData.viewer.panelOn;
					this.panelOn = true;
				}

				this.blockUI.stop();
			},
			(err: any) => {
				console.error("Document failed to load:", err);
				this.blockUI.stop();
			}
		);
	}

	private logDownloadAnalytics(): void {
		let params = {
			'id': this.product.id,
			'title': this.product.title,
			'browse': this.fullRecordMetadata['browsepath']
		};

		this.analyticsService.trackEvent('event', this.analyticsService.downloadCategory, {
			event_category: 'engagement',
			event_label: 'Downloaded a document',
			event_description: params
		});
	}

	private logVisitationAnalytics(): void {
		let params = {
			'id': this.product.id,
			'title': this.product.title,
			'browse': this.fullRecordMetadata['browsepath']
		};

		this.analyticsService.trackEvent('event', this.analyticsService.viewCategory, {
			event_category: 'engagement',
			event_label: 'Visited a document',
			event_description: params
		});
	}

	private processVisited(): void {
		// Log this view in the user's visted list
		let userData = this.userService.getLocalStorageData();
		userData.visited[this.product.id] = 1;
		this.userService.setLocalStorageData(userData);

		this.logVisitationAnalytics();
	}

	private processPages(): void {
		if (this.documentData['arcGISQueryURL']) {
			// We don't need to display any pages
			this.pages = [];
		}

		// Apply the internal page filter
		if (this.pages.length > 0) {
			let _pages = [];
			for (let i in this.pages) {
				if (this.internalPageFilter(this.pages[i])) {
					_pages.push(this.pages[i]);
				}
			}
			this.pages = _pages;
		}

		let sortPage = (a: any, b: any) => {
			if (a.pageNumber > b.pageNumber || a.pageNum > b.pageNum) {
				return 1;
			} else if (b.pageNumber > a.pageNumber || b.pageNum > a.pageNum) {
				return -1;
			} else {
				if (a.secondaryNumber && b.secondaryNumber) {
					if (a.secondaryNumber > b.secondaryNumber) {
						return 1;
					} else if (a.secondaryNumber < b.secondaryNumber) {
						return -1;
					} else {
						if (a.type === "sheet" && b.type === "overlay") {
							return -1;
						} else {
							return 1;
						}
					}
				}
				return 0;
			}
		};

		// if it's a package type, it will set a flag to treat single document viewer to be fixed
		if (this.documentData?.['documentType'] === "MULTIPAGE_PKG") {
			this.package.pages = this.documentData['documentPages']?.sort(sortPage);
			this.package.page = 1;
		}
		else {
			this.package.pages = [];
		}

		if (this.pages == null || this.pages.length == 0) {
			this.processPage(null);
		} else if (this.albumView) {
			this.selectPage(this.selectedPage);
		} else {
			this.selectPage(0);
		}

		this.pages = this.pages.sort(sortPage);
		if (this.pageInfo && this.pageInfo.pageNumber) {
			let i = this.pageInfo.pageNumber;
			if (this.pages && this.pages.length > i) {
				this.selectPage(i);
			}
		}
	}

	private getPageDescription(page: any): PageDescription {
		let type = "IMAGE";
		let mime = "image/jpeg";

		if (page == null) {
			// Assume single page doc or multi-page with no pages
			mime = this.documentData['documentType'];

			if (this.pages && this.pages.length == 0 && (mime === "image/tiff" || mime === "MULTIPAGE_PKG")) {
				mime = "metadata"; // We display metadata for a pageless multipkg type until user adds a page
			}
		}
		else {
			mime = page.fileType || mime;
		}

		if (mime.toLowerCase().indexOf("image") > -1) {
			type = "IMAGE";
		} else if (mime.indexOf("text") > -1) {
			type = "TEXT";
		} else if (mime.toLowerCase().indexOf("video") > -1) {
			type = "VIDEO";
		} else if (mime.toLowerCase().indexOf("audio") > -1) {
			type = "AUDIO";
		} else if (mime.toLowerCase().indexOf("metadata") > -1) {
			type = "METADATA";
		} else if (this.documentData['arcGISQueryURL']) {
			type = "ARCGIS_LAYER";
		} else if (mime.toLowerCase().indexOf("vnd.google-earth.kml+xml") > -1) {
			type = "VECTOR";
		} else if (mime.toLowerCase().indexOf("pdf") > -1
			|| mime.toLowerCase().indexOf("msword") > -1
			|| mime.toLowerCase().indexOf("powerpoint") > -1
			|| mime.toLowerCase().indexOf("pptx") > -1
			|| mime.toLowerCase().indexOf("ntf") > -1
			|| mime.toLowerCase().indexOf("rtf") > -1
			|| mime.toLowerCase().indexOf("xml") > -1
			|| mime.toLowerCase().indexOf("ms-excel") > -1) {
			type = "TEXT";
		} else if (mime.toLowerCase().indexOf("octet-stream") > -1) {
			// Treat this specifically as OTHER since it is a generic mime type for binary files
			type = "OTHER";
		}
		else {
			if (this.documentData) {
				type = this.documentData['contentType'];
			} else {
				type = "OTHER";
			}
		}

		let pageDescription: PageDescription = {
			type: type,
			mime: mime,
			currView: 'metadata'
		}

		console.log(`Processing page; type = ${pageDescription.type}, mime = ${pageDescription.mime}`);

		return pageDescription;
	}

	private processPage(page: any): void {
		this.page = page;
		if (page == null) {
			this.productPageMetadata = {};
		}
		else {
			if (this.configService.getConfig()?.viewer?.pageMetadataEnabled == false || this.albumView) {
				this.productPageMetadata = {};
			}
			else {
				this.productPageMetadata = page.productPageMetadata == null
					? (page?.documentData?.productPageMetadata ? page.documentData.productPageMetadata : {})
					: page.productPageMetadata;

				this.copyPageMetadata = cloneDeep(this.productPageMetadata);
			}
		}

		// TODO can we remove this? Why track it separately?
		this.pdfView = false;
		this.pageDescription = this.getPageDescription(page);

		switch (this.pageDescription.type) {
			case "TEXT":
				this.pageDescription.currView = "text";

				// handle PDFs, emails, etc. differently here
				if ((this.documentData && this.documentData['documentType'] === 'application/pdf')
					|| (this.pageDescription.mime && this.pageDescription.mime.toLowerCase().indexOf("pdf") > -1)
				) {
					// We need to explicitly check that this is a PDF inside of a multipage package type.
					this.pdfView = this.documentData['documentType'] === 'image/tiff' ? false : true;
					break;
				}
				break;

			case "VIDEO":
				this.pageDescription.currView = "video";
				break;

			case "IMAGE":
				if (this.pageDescription.mime === 'image/gif') {
					this.pageDescription.currView = "image-default";
				}
				else {
					this.pageDescription.currView = "image";
				}
				break;

			case "AUDIO":
				this.pageDescription.currView = "audio";
				break;

			case "VECTOR":
				// This shows a projected image on a map
				if (this.pageDescription.mime == "application/vnd.google-earth.kml+xml") {
					this.pageDescription.currView = "map";
					this.pageDescription.type = this.pageDescription.mime;
				}
				break;

			case "ARCGIS_LAYER":
				// This shows a geo feature layer and dataset on a map
				if (!this.documentData['arcGISQueryURL']) {
					this.pageDescription.currView = "metadata";
					break;
				}

				if (this.documentData['arcGISQueryURL'].indexOf("aw-server") > -1) {
					this.pageDescription.currView = "arcgis";
				} else {
					// Opens an external third-party viewer. Requires CORS considerations.
					this.pageDescription.currView = "external_arcgis";
				}
				break;

			case "METADATA":
				this.pageDescription.currView = "metadata";
				if (this.panelMode = "productMetadata") {
					this.panelOn = false;
					this.panelMode = "";
				}
				break;

			default:
				this.pageDescription.currView = "metadata";
				if (this.panelMode = "productMetadata") {
					this.panelOn = false;
					this.panelMode = "";
				}
		}

		console.log(`Current View: ${this.pageDescription.currView}`);
	}

	selectPage(index: number): void {
		let oldpage = this.selectedPage;
		this.selectedPage = index;

		this.onPageSelected(index);

		if (this.pdfView) {
			this.pageDescription.currView = "text";
			// can reset if it's a package but remains normal if not 
			//			this.pdf.currentPage = this.package.pages.length > 0 ? 1 : index + 1;
			//			this.getCurrentPdfPage();
		}
		else {
			if (oldpage == index) {
				this.processPage(this.getPages()[index]);
			}
		}
	}

	// Migrated from: this.$watch("selectedPage", function (value) {}
	private onPageSelected(value: number): void {
		if (value != null && this.pages && this.pages.length > 0) {
			let currPage = null;
			if (value == null) {
				let allPages = this.getPages();
				if (allPages.length == 0) {
					return;
				}
				currPage = allPages[0];
				this.selectedPage = this.pages.indexOf(currPage);
			} else {
				currPage = this.pages[value];
			}

			if (!currPage) {
				return;
			}

			let downloadLink = "";
			if (currPage.type === "overlay" || currPage.type === "sheet") {
				downloadLink = formatString(this.configService.getConfig().document.documentDownloadPageSecondaryURL, this.product.id, currPage.pageNumber, currPage.type, currPage.index);
			}
			this.downloadDocumentPageLink = downloadLink;

			let pages = this.getPages();
			this.hasPrev = pages.length == 1 || pages.indexOf(currPage) == 0 ? false : true;
			this.hasNext = pages.length == 1 || pages.indexOf(currPage) == pages.length - 1 ? false : true;

			this.processPage(currPage);
		}

		this.hasNext = false;
		this.hasPrev = false;
	}

	private processSearchResults(): void {
		this.searchResults.total = this.searchService.results?.total || 0;
		this.searchResults.records = this.searchService.results?.records || [];
		this.searchResults._index = this.searchResults.current - ((this.searchService.pageIndex - 1) * this.searchService.pageSize);
		let selected = this.searchResults.records[this.searchResults._index - 1];

		this.loadDocument(selected.id, this.pageInfo);
	}

	/**
	 * Called on prev/next paging to load the requested result.
	 */
	loadSearchResult(resultNumber: number): void {
		if (resultNumber < 1 || resultNumber > this.searchResults.total) {
			return;
		}

		this.searchResults.current = resultNumber;

		let oldSelected = this.searchResults.records[this.searchResults._index - 1];

		if (this.userPermissions["ROLE_PRODUCT_UPDATE"]) {
			this.productService.unlockProduct(oldSelected.id);
		}

		this.searchResults._index = this.searchResults.current - ((this.searchService.pageIndex - 1) * this.searchService.pageSize);

		if (this.searchResults._index > this.searchResults.records.length) {
			// go to next page...
			this.searchService.pageIndex++;
			this.searchService.doSearch({ libraries: this.searchService.libraries });
		} else if (this.searchResults._index <= 0) {
			// go to prev page...
			this.searchService.pageIndex--;
			this.searchService.doSearch({ libraries: this.searchService.libraries });
		} else {
			let selected = this.searchResults.records[this.searchResults._index - 1];
			this.loadDocument(selected.id, this.pageInfo);
		}
	}

	/**
	 * Loads the Records Managment record for the current document, if available.
	 */
	private loadRMRecord(): void {
		let record = (this.searchResults.records as any[])[this.searchResults.current - 1];
		if (record && record.rm_record_id) {
			this.isRecord = true;
		}
	}

	/**
	 * Filters pages using internal flags.
	 */
	private internalPageFilter(item: any): boolean {
		if (this.internalFilters.length == 0) {
			return true;
		}

		if (item?.productPageMetadata?.tags?.length > 0) {
			for (let i in item.productPageMetadata.tags) {
				if (this.internalFilters.includes(item.productPageMetadata.tags[i])) {
					return false;
				}
			}
		} else if (this.internalFilters.includes("<EMPTY>")) {
			return true;
		}

		return true;
	}

	/**
	 * Filters pages using user-provided flags.
	 */
	pageFilter(item: any): boolean {
		if (this.filters.length == 0) {
			return true;
		}

		if (item?.productPageMetadata?.tags?.length > 0) {
			let hashy: { [key: string]: number } = {};
			for (let i in item.productPageMetadata.tags) {
				hashy[item.productPageMetadata.tags[i]] = 1;
			}

			for (let i in this.filters) {
				let filter = this.filters[i];
				if (hashy[filter] == null) {
					return false;
				}
			}

			return true;
		}

		return false;
	}

	private getPages(): any[] {
		let pages = [];
		for (let i in this.pages) {
			if (this.pageFilter(this.pages[i])) {
				pages.push(this.pages[i]);
			}
		}
		return pages;
	}

	private checkDownloadAccess(): void {
		if (this.userPermissions['ROLE_PRODUCT_DOWNLOAD']) {
			this.docPerm = "download";
		} 
	}

	private updateUserCachePanel(): void {
		let userData = this.userService.getLocalStorageData();
		userData.viewer.panelOn = this.panelMode;
		this.userService.setLocalStorageData(userData);
	}

	projectOCR(): void {
		this.pageDescription.currView = "ocr";
	}

	returnImage(): void {
		this.selectPage(this.selectedPage);
	}

	togglePanel(panel: string): void {
		this.pageMetadataEdit = false;

		if (this.panelMode === panel) {
			this.panelMode = "";
			this.panelOn = false;
		}
		else {
			this.panelMode = panel;
			this.panelOn = true;
		}

		this.updateUserCachePanel();
	}

	initializepageMetadataEdit(open: boolean): void {
		this.currTag = null;
		this.copyPageMetadata = cloneDeep(this.productPageMetadata);
		this.pageMetadataEdit = open;
	}

	savePageMetadataEdit(): void {
		if (this.albumView) {
			let page = this.pages[this.selectedPage].pageNumber || 0;
			this.albumsService.updateAlbumPageMetadata(this.albumView, this.pages[this.selectedPage].productId, page, this.copyPageMetadata).then(
				(response: any) => {
					this.productPageMetadata = this.copyPageMetadata;
					this.pageMetadataEdit = false;
					this.pages[this.selectedPage].productPageMetadata = this.copyPageMetadata;
					this.alertsService.success({
						message: this.LOCALIZATION.updateAlbumItemSuccess
					});
				},
				(err: any) => {
					console.error("Failed to save page metadata:", err);
				}
			);
		} else {
			this.productService.updatePageMetadata(this.product.id, this.pages[this.selectedPage].pageNumber, this.copyPageMetadata).then(
				(response: any) => {
					this.productPageMetadata = this.copyPageMetadata;
					this.pageMetadataEdit = false;
					this.pages[this.selectedPage].productPageMetadata = this.copyPageMetadata;
					this.alertsService.success({
						message: "Page metadata updated successfully."
					});
				},
				(err: any) => {
					console.error("Failed to save page metadata:", err);
				}
			);
		}
	}

	addTag(tag?: string | null): void {
		if (tag && tag.trim() !== "") {
			tag = tag.trim();
			let hashy: { [key: string]: number } = {};
			for (let i in this.copyPageMetadata['tags']) {
				let tagg = this.copyPageMetadata['tags'][i];
				hashy[tagg] = 1;
			}
			if (hashy[tag] == null) {
				if (!this.copyPageMetadata['tags']) {
					this.copyPageMetadata['tags'] = [];
				}
				this.copyPageMetadata['tags'].push(tag);
			}
		}

		this.currTag = null;
	}

	removeTag(index: number): void {
		this.copyPageMetadata['tags'].splice(index, 1);
	}

	addFilter(filter?: string | null): void {
		if (filter && filter.trim() !== "") {
			filter = filter.trim();
			let hashy: { [key: string]: number } = {};
			for (let i in this.filters) {
				let fit = this.filters[i];
				hashy[fit] = 1;
			}
			if (hashy[filter] == null) {
				if (!this.filters) {
					this.filters = [];
				}
				this.filters.push(filter);
			}
		}

		this.filterInput = null;
	}

	removeFilter(index: number): void {
		this.filters.splice(index, 1);
	}

	onDownload(): void {
		this.logDownloadAnalytics();

		this.alertsService.info({
			title: this.LOCALIZATION.downloadStarted,
			message: this.LOCALIZATION.processingWaitMessage,
			delay: 10000
		});
	}

	/**
	 * Prints the product.
	 */
	printProduct(): void {
		this.productService.getPrintProductId(this.product.id).then(
			(printId: string) => {
				this.productService.printProduct(this.product.id, printId);
			},
			(err: any) => {
				console.error("Failed to print product:", err);
				this.alertsService.error({
					message: this.LOCALIZATION.printDocumentError
				});
			}
		);
	}

	/**
	 * Prints the product metadata.
	 */
	printProductMetadata(): void {
		let payload = {
			format: 'txt',
			allMetadata: true
		};

		this.productService.printProductMetadata([this.product.id], payload).then(
			(response: string) => {
				this.metadataService.printMetadata(response, this.product.title);
			},
			(err: any) => {
				console.error("Failed to print metadata:", err);
				this.alertsService.error({
					message: this.LOCALIZATION.printMetadataError
				});
			}
		);
	}

	/**
	 * Displays the product in the map view.
	 */
	openInMap(): void {
		let record = this.searchResults.records[this.searchResults.current - 1];
		this.mapService.openInMap(record, false);
		this.close();
	}

	/**
	 * Displays the record management metadata for the product.
	 */
	viewRMQuickInfo(): void {
		const modalRef = this.ngbModal.open(RecordQuickViewComponent);
		modalRef.componentInstance.productId = this.product.id;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the album manager for the product.
	 */
	addToAlbum(): void {
		const modalRef = this.ngbModal.open(AlbumItemComponent);

		let productData = {
			uuid: this.product.id,
			name: '',
			type: this.pages.length > 0 ? "STANDARD" : undefined,
			page: this.pages.length > 0 ? this.pages[this.selectedPage].pageNumber : undefined,
			sheet: this.pages.length > 0 ? 0 : undefined
		};

		modalRef.componentInstance.albumItems = {
			mode: 'add',
			records: [productData]
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the product share view.
	 */
	shareProduct(): void {
		const modalRef = this.ngbModal.open(ProductShareComponent);
		modalRef.componentInstance.product = {
			id: this.product.id,
			pageNumber: (this.pages && this.pages.length > 0 ? this.pages[this.selectedPage].pageNumber : null)
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the product version manager.
	 */
	openRevisionHistory(): void {
		const modalRef = this.ngbModal.open(RevisionHistoryComponent);
		modalRef.componentInstance.product = {
			id: this.product.id,
			title: this.product.title
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the product/page email view.
	 */
	email(isPage: boolean = false): void {
		const modalRef = this.ngbModal.open(EmailProductsComponent);

		if (!isPage) {
			modalRef.componentInstance.productGroup = {
				ids: [this.product.id],
				title: this.LOCALIZATION.emailDocument
			};
		}
		else {
			modalRef.componentInstance.productGroup = {
				id: this.product.id,
				page: this.pages[this.selectedPage].pageNumber,
				title: this.LOCALIZATION.emailPage
			};
		}

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Requests OCR processing for the product file.
	 */
	ocrDocument(): void {
		const modalRef = this.ngbModal.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'ocrDocument',
			title: this.LOCALIZATION.ocrDocumentHeader,
			message: this.LOCALIZATION.ocrDocumentMessage,
			type: 'primary'
		};

		modalRef.result.then(
			(payload: any) => {
				if (payload.result?.answer === 'ok') {
					this.productService.ocrDocuments([this.product.id]).then(
						(response: any) => {
							this.alertsService.success({
								message: this.LOCALIZATION.processingWaitMessage,
								delay: 5000
							});
						},
						(err: any) => {
							console.error("Failed to ocr product:", err);
							this.alertsService.error({
								message: this.LOCALIZATION.ocrDocumentError
							});
						}
					);
				}
			},
			(reason: any) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Sets the current page as the primary product page.
	 */
	setPrimaryPage(): void {
		const modalRef = this.ngbModal.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'setPrimaryPage',
			title: this.LOCALIZATION.primaryPageHeader,
			message: this.LOCALIZATION.primaryPageMessage,
			type: 'danger'
		};

		modalRef.result.then(
			(payload: any) => {
				if (payload.result?.answer === 'ok') {
					if (this.pages && this.pages.length > 0) {
						this.alertsService.info({
							message: this.LOCALIZATION.primaryPageInfo,
							delay: 5000
						});

						this.productService.setPrimaryProductPage(this.product.id, this.pages[this.selectedPage].pageNumber).then(
							(response: any) => {
								this.alertsService.success({
									message: this.LOCALIZATION.searchUpdatesConfirmationMessage,
									delay: 5000
								});
								// this.close();
							},
							(err: any) => {
								console.error("Failed to set primary product page:", err);
								this.alertsService.error({
									message: this.LOCALIZATION.primaryPageError
								});
							}
						);
					} else {
						this.alertsService.error({
							message: this.LOCALIZATION.primaryPageErrorNotSupported
						});
					}
				}
			},
			(reason: any) => {
				console.log('Modal dismissed');
			}
		);
	}

	addPage() {
		this.pageFileInputRef.nativeElement.click();
	}

	onFilesSelected(files: File[]) {
		for (const file of files) {
			// add page
			this.alertsService.info({
				message: this.LOCALIZATION.addPageInfo,
				delay: 5000
			});
			this.productService.addProductPage(this.product.id.toString(), file).then(
				(response: any) => {
					this.alertsService.success({
						message: this.LOCALIZATION.addPageSuccess,
						delay: 5000
					});
				},
				(err: any) => {
					console.error("Failed to add product page:", err);
					this.alertsService.error({
						message: this.LOCALIZATION.addPageError
					});
				}
			);
		}
	}
	
	/**
	 * Deletes the current page from the product.
	 */
	deletePage(): void {
		const modalRef = this.ngbModal.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'deletePage',
			title: this.LOCALIZATION.deletePageHeader,
			message: this.LOCALIZATION.deletePageMessage,
			type: 'danger'
		};

		modalRef.result.then(
			(payload: any) => {
				if (payload.result?.answer === 'ok') {
					this.alertsService.info({
						message: this.LOCALIZATION.deletePageInfo,
						delay: 5000
					});
					this.productService.deleteProductPage(this.product.id, this.pages[this.selectedPage].pageNumber).then(
						(response: any) => {
							this.alertsService.success({
								message: this.LOCALIZATION.searchUpdatesConfirmationMessage,
								delay: 5000
							});
						},
						(err: any) => {
							console.error("Failed to delete product page:", err);
							this.alertsService.error({
								message: this.LOCALIZATION.deletePageError
							});
						}
					);
				}
			},
			(reason: any) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Deletes the product.
	 */
	deleteDocument(): void {
		const modalRef = this.ngbModal.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'deleteDocument',
			title: this.LOCALIZATION.deleteDocumentHeader,
			message: this.LOCALIZATION.deleteDocumentMessage,
			type: 'danger'
		};

		modalRef.result.then(
			(payload: any) => {
				if (payload.result?.answer === 'ok') {
					this.productService.deleteDocuments([this.product.id]).then(
						(response: any) => {
							this.alertsService.success({
								message: this.LOCALIZATION.searchUpdatesConfirmationMessage,
								delay: 5000
							});
						},
						(err: any) => {
							console.error("Failed to delete product:", err);
							this.alertsService.error({
								message: this.LOCALIZATION.deleteDocumentError
							});
						}
					);
				}
			},
			(reason: any) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the product thumbnail loader.
	 */
	openThumbnailLoader(): void {
		const modalRef = this.ngbModal.open(ThumbnailLoaderComponent);

		modalRef.componentInstance.product = {
			id: this.product.id,
			title: this.product.title
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the citation generator.
	 */
	createCitations(): void {
		const modalRef = this.ngbModal.open(CitationComponent);

		modalRef.componentInstance.productGroup = [this.fullRecordMetadata];

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the product feedback dialog.
	 */
	openProductFeedback(): void {
		const modalRef = this.ngbModal.open(ProductFeedbackComponent);

		modalRef.componentInstance.product = {
			id: this.product.id,
			title: this.product.title
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the search information viewer.
	 */
	searchTerms(): void {
		const modalRef = this.ngbModal.open(SearchTermsComponent);
		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	onGeotag(): void {
		const modalRef = this.ngbModal.open(GeoTagComponent, {
			size: 'lg',
			windowClass: 'kv-utility-modal kv-utility-large-modal-window',
			animation: false
		});

		modalRef.componentInstance.productGroup = {
			commit: true,
			recordId: this.product.id
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	// Bound in the template
	onSubComponentCancel(componentName: string) {
		this.togglePanel(componentName)
	}

	// Bound in the template
	onCloseMainViewer(): void {
		this.close();
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}

	
	// ---------------------------------------------
	// Non-standard viewer functions
	// ---------------------------------------------
	
	/**
	 * Loads the standard KV product metadata.
	 */
	loadRecord(): void {
		this.searchService.getRecord(this.product.id).then(
			(response: any) => {
				let property = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name === "IS_HIGHLIGHT_VIEW_FIELD" });
				if (property.value === "yes") {
					this.viewerService.getHighlightedMetadataRecord(this.product.id).then(
						(highlights: any) => {
							this.processMetadata(response, highlights);
						},
						(err: any) => {
							console.error("Failed to load product metadata", err);
							this.alertsService.error({
								message: "Failed to retrieve metadata for this document."
							});
						}
					);
				} else {
					this.processMetadata(response);
				}
			},
			(err: any) => {
				console.error("Failed to load product metadata", err);
				this.alertsService.error({
					message: "Failed to retrieve metadata for this document."
				});
			}
		);
	}

	private processMetadata(result: any, highlights?: any) {
		let hl: { [key: string]: any } = {};
		if (highlights && highlights["aw:record"]["print"]) {
			hl = highlights["aw:record"]["print"];
		}

		let productHash = result["csw:GetRecordByIdResponse"].iStoreRecord;
		let productInfo: { [key: string]: any } = {};
		let validFields = this.userService.user.properties.awfieldObjectList.filter((x: any) => { return x.isAvailable && x.isMetadata });

		for (let p = 0; p < validFields.length; p++) {
			let field = validFields[p];

			if (!productHash[field.name]) {
				continue;
			}

			productInfo[field.displayName] = {
				label: field.displayName,
				value: hl[field.name] ? hl[field.name] : productHash[field.name],
				maxLength: (field.isLink ? -1 : 220),
				links: null,
				name: field.name
			};

			productInfo[field.displayName].cleanedValue = this.metadataService.cleanValue(productInfo[field.displayName].value);
			productInfo[field.displayName].isValidCollection =
				this.metadataService.isValidFieldCollectionValue(productInfo[field.displayName].name, productInfo[field.displayName].cleanedValue);

			if (field.type == "DATE") {
				productInfo[field.displayName].value = dayjs(productInfo[field.displayName].value).format(this.dateFormatDisplay);
			}

			if (field.name == "requires") {
				productInfo[field.displayName].warning = true;
			}

			if (field.isLink) {
				productInfo[field.displayName].links = this.metadataService.linksFromString(productHash[field.name],field.name);
			} else {
				if (productInfo[field.displayName].value) {
					productInfo[field.displayName].value = productInfo[field.displayName].value.replace(/; /g, ';').replace(/;/g, '; ');
				}
			}
		}

		this.record = productInfo;
	}

	/**
	 * Executes a search using the field and value.
	 */
	resultFieldSearch(field: string, value: string): void {
		console.log(`Searching for: ${field}, with value: ${value}`);

		let payload = {
			field: field,
			value: value,
			source: 'metadata-view'
		};
		this.searchService.broadcastSearchResultFieldEvent(payload);
	}

	displayLinkMsg(link: string): void {
		if (link.startsWith("file://")) {
			const modalRef = this.ngbModal.open(CopyLinkComponent);
			modalRef.componentInstance.link = link;

			modalRef.result.then(
				(payload) => {
					console.log('Modal dismissed');
				},
				(reason) => {
					console.log('Modal dismissed');
				}
			);
		}
	}
	
	openViewer(pageNumber: number): void {
		const modalRef = this.ngbModal.open(DocViewComponent, {
			windowClass: "viewer-modal-window"
		});
		
		modalRef.componentInstance.productId = this.product.id;
		
		// The pageNumber data appears to be off by one
		modalRef.componentInstance.pageInfo = {
			pageNumber: pageNumber - 1
		};

		modalRef.result.then(
		    (payload) => {
		        console.log('Modal dismissed');
		    },
		    (reason) => {
		        console.log('Modal dismissed');
		    }
		);
	}


	// ---------------------------------------------
	// Memorial View Custom
	// ---------------------------------------------

	isHonorRoll(): boolean {
		return this.fullRecordMetadata['honor_roll']
			&& (this.fullRecordMetadata['honor_roll'] === 1 || this.fullRecordMetadata['honor_roll'] === '1');
	}
	
	private async initializeABMCConfiguration(): Promise<void> {
		await this.loadCemeteries();
		this.loadSocialMediaForProduct();
	}
	
	private async loadCemeteries(): Promise<void> {
		return firstValueFrom(this.http.get('/app/aw-ui-angular/assets/custom/abmc/config/cemeteries.json'))
			.then((config: any) => {
				this.cemeteries = config;
			}).catch((error: any) => {
				console.error("Failed to load cemeteries: ", error);
			});
	}
	
	/**
	 * This is product-level social media, separate from the overall
	 * system social media config managed in AdminUI.
	 * These socials provide direct links to a product.
	 */
	private loadSocialMediaForProduct(): void {
//		Use a direct link to the product, eg:
//		https://awdev00/s?docid=6109		
		const baseUrl = location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '');
		const shareUrl = `${baseUrl}/s?docid=${this.product.id}`;		

		this.socials = {
			'x' : "https://twitter.com/intent/tweet?url=" + encodeURIComponent(shareUrl),
			'facebook' : "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(shareUrl),
			'linkedin': "https://www.linkedin.com/sharing/share-offsite/?url="+ encodeURIComponent(shareUrl),
			'instagram':  "https://instagram.com/usabmc"
		};
	}
	
	isFeatureEnabled(feature: string): boolean {
		return this.featureService.isFeatureEnabled(feature);
	}
	
	private initCarousel(): void {
		this.currentCarouselPageIndex = 0;
		this.goToIndex(this.currentCarouselPageIndex);
	}

	private goToIndex(index: number): void {
		if (! this.carouselViewerRef) return;
		
		// Move the carousel to simulate paging through the images
		const carousel = this.carouselViewerRef.nativeElement;
		const offset = -index * 25;
		carousel.style.transform = `translateX(${offset}%)`;

		this.currentCarouselPageIndex = index;
	}

	carouselPrev(): void {
		if (this.currentCarouselPageIndex > 0) {
			this.goToIndex(this.currentCarouselPageIndex - 1);
		} else {
			this.goToIndex(this.pages.length - 4); // FIXME what is magic number 4?
		}
	}

	carouselNext(): void {
		if (this.currentCarouselPageIndex < this.pages.length - 4) {
			this.goToIndex(this.currentCarouselPageIndex + 1);
		} else {
			this.goToIndex(0);
		}
	}
	
	private loadPageAssets(): void {
		const cssHref = `/app/aw-ui-angular/assets/custom/${this.custId}/css/${this.featureId}.css`;

		if (!document.querySelector(`link[href="${cssHref}"]`)) {
			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = cssHref;
			document.head.appendChild(link);
		}
	}
	
	// FIXME: FOR TESTING ONLY - REMOVE ME
//	private fillTESTMetadata(): void {
//		this.getFullProductMetadata().then(
//			(data: any) => {
//				for (const key in data) {
//					if (Array.isArray(data[key])) {
//						data[key] = data[key].join(';');
//					}
//				}
//				this.fullRecordMetadata = data;
//			},
//			(err: any) => {
//				console.error("Failed to fetch full metadata: ", err);
//			}
//		);
//	}
//	private async getFullProductMetadata(): Promise<any> {
//		let url = "/aw-server/rest/product/action/getMetadata?id=" + this.product.id;
//
//		try {
//			const response = await firstValueFrom(this.http.get<any>(url, {}));
//			return response;
//		} catch (error) {
//			return Promise.reject(error);
//		}
//	}	
	// END: REMOVE ME
}
